import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { getKeycloak } from './keycloak';

// Use relative URL in production, or the env variable in development
const baseURL = (import.meta as any).env?.VITE_API_URL || '/api';

const api: AxiosInstance = axios.create({
  baseURL,
});

// Add token to requests
api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  // First try to get token from window (set by keycloak.ts after backend exchange)
  // This is more reliable than calling keycloak.updateToken() which may not work
  // after backend token exchange
  const windowToken = (window as any).__keycloakToken__;
  if (windowToken) {
    config.headers.Authorization = `Bearer ${windowToken}`;
  } else {
    // Fallback to Keycloak instance
    try {
      const keycloak = getKeycloak();
      if (keycloak?.token) {
        config.headers.Authorization = `Bearer ${keycloak.token}`;
      }
    } catch (error) {
      // Keycloak not initialized yet, request will go without auth
    }
  }

  // Set Content-Type for non-FormData requests
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }

  return config;
});

// Suppress console errors for 404s (files not existing yet is normal)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 404 errors for CSV files are expected (files don't exist yet)
    if (error.response?.status === 404 && error.config?.url?.includes('/csv/')) {
      // Don't log these errors, just reject silently
      return Promise.reject(error);
    }
    // For other errors, log them
    if (error.response?.status >= 500) {
      console.error('API Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export interface FileItem {
  key: string;
  name: string;
  size: number;
  lastModified: string;
  type: 'file' | 'folder';
  folder?: string;
  metadata?: Record<string, string>;
  versionInfo?: {
    currentVersion: string;
    totalVersions: number;
    hasVersionHistory: boolean;
  };
}

export interface FolderStructure {
  categories: FileItem[];
  courses: FileItem[];
  competency_frameworks: FileItem[];
  learning_path_templates: FileItem[];
}

// Content Folder APIs
export const contentApi = {
  // Get all content structure
  getStructure: async (): Promise<FolderStructure> => {
    const { data } = await api.get<FolderStructure>('/content/structure');
    return data;
  },

  // Get files in a specific folder
  getFolderContents: async (folder: string): Promise<FileItem[]> => {
    const { data } = await api.get<FileItem[]>(`/content/${folder}`);
    return data;
  },

  // Get file content
  getFileContent: async (folder: string, filename: string): Promise<string> => {
    const { data } = await api.get<string>(`/content/${folder}/${filename}`, {
      headers: { Accept: 'text/plain' },
    });
    return data;
  },

  // Download file as binary (for .mbz and other binary files)
  downloadFile: async (folder: string, filename: string): Promise<Blob> => {
    const { data } = await api.get(`/content/${folder}/${filename}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // Upload/Update file
  uploadFile: async (folder: string, filename: string, content: string | ArrayBuffer | File): Promise<void> => {
    const formData = new FormData();
    // If content is already a File, use it directly; otherwise create a new File
    const file = content instanceof File ? content : new File([content], filename);
    formData.append('file', file);
    await api.post(`/content/${folder}`, formData);
  },

  // Upload multiple files
  uploadFiles: async (folder: string, files: File[]): Promise<void> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
    });
    await api.post(`/content/${folder}`, formData);
  },

  // Delete file
  deleteFile: async (folder: string, filename: string): Promise<void> => {
    // URL encode the filename to handle nested paths like "3/38/package.zip"
    await api.delete(`/content/${folder}/${encodeURIComponent(filename)}`);
  },

  // Delete multiple files
  deleteFiles: async (folder: string, filenames: string[]): Promise<void> => {
    await api.delete(`/content/${folder}`, { data: { filenames } });
  },

  // Get files with version information
  getFolderContentsWithVersions: async (folder: string, environmentId?: string): Promise<FileItem[]> => {
    const params = environmentId ? { environmentId } : {};
    const { data } = await api.get<FileItem[]>(`/content/${folder}/with-versions`, { params });
    return data;
  },

  // Get all versions of a specific file
  getFileVersions: async (folder: string, filename: string): Promise<VersionMetadata[]> => {
    const encodedFilename = encodeURIComponent(filename);
    const { data } = await api.get<VersionListResponse>(`/content/${folder}/${encodedFilename}/versions`);
    return data.versions;
  },
};

// Version Control APIs
export interface VersionMetadata {
  version: string;
  s3Key: string;
  timestamp: string;
  uploadedBy?: string;
  description?: string;
  size: number;
  fileHash?: string;
  parentVersion?: string;
  isCurrent?: boolean;
  tags?: string[];
  metadata?: Record<string, string>;
}

export interface VersionListResponse {
  versions: VersionMetadata[];
}

export interface UploadVersionedOptions {
  uploadedBy?: string;
  description?: string;
  tags?: string[];
}

export const versionApi = {
  // Get version history for a file
  getVersionHistory: async (folder: string, filename: string): Promise<VersionMetadata[]> => {
    // URL encode the filename to handle special characters like slashes in CMI5 activity paths
    const encodedFilename = encodeURIComponent(filename);
    const { data } = await api.get<VersionListResponse>(`/content/${folder}/${encodedFilename}/versions`);
    return data.versions;
  },

  // Download a specific version
  downloadVersion: async (folder: string, filename: string, version: string): Promise<Blob> => {
    // URL encode the filename to handle special characters like slashes in CMI5 activity paths
    const encodedFilename = encodeURIComponent(filename);
    const { data } = await api.get(`/content/${folder}/${encodedFilename}/versions/${version}/download`, {
      responseType: 'blob',
    });
    return data;
  },

  // Restore a specific version (make it current)
  restoreVersion: async (folder: string, filename: string, version: string, uploadedBy?: string): Promise<void> => {
    // URL encode the filename to handle special characters like slashes in CMI5 activity paths
    const encodedFilename = encodeURIComponent(filename);
    await api.post(`/content/${folder}/${encodedFilename}/versions/${version}/restore`, { uploadedBy });
  },

  // Delete a specific version
  deleteVersion: async (folder: string, filename: string, version: string): Promise<void> => {
    // URL encode the filename to handle special characters like slashes in CMI5 activity paths
    const encodedFilename = encodeURIComponent(filename);
    await api.delete(`/content/${folder}/${encodedFilename}/versions/${version}`);
  },

  // Upload files with versioning
  uploadVersioned: async (folder: string, files: File[], options: UploadVersionedOptions = {}): Promise<VersionMetadata[]> => {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('file', file);
    });
    if (options.uploadedBy) {
      formData.append('uploadedBy', options.uploadedBy);
    }
    if (options.description) {
      formData.append('description', options.description);
    }
    if (options.tags) {
      options.tags.forEach(tag => formData.append('tags', tag));
    }
    const { data } = await api.post<{ versions: VersionMetadata[] }>(`/content/${folder}/versioned`, formData);
    return data.versions;
  },

  // Migrate a single file to versioned structure
  migrateFile: async (folder: string, filename: string): Promise<void> => {
    // URL encode the filename to handle special characters like slashes in CMI5 activity paths
    const encodedFilename = encodeURIComponent(filename);
    await api.post(`/content/${folder}/${encodedFilename}/migrate`);
  },

  // Migrate all files in a folder to versioned structure
  migrateFolder: async (folder: string): Promise<{ migrated: number; total: number; failures?: any[] }> => {
    const { data } = await api.post<{ migrated: number; total: number; failures?: any[] }>(`/content/${folder}/migrate-all`);
    return data;
  },
};

// Base prefix APIs
export interface BasePrefixResponse {
  prefixes: string[];
  current: string;
}

export interface EnvironmentWithBucket {
  environmentId: string;
  environmentName: string;
  prefix: string;
  bucketId: string;
  bucketName: string;
  bucketConfig: {
    bucket: string;
    region: string;
    endpoint?: string;
  };
}

export const basePrefixApi = {
  // Get available base prefixes
  getBasePrefixes: async (): Promise<string[]> => {
    const { data } = await api.get<string[]>('/base-prefixes');
    return data;
  },

  // Set base prefix (legacy - use setEnvironmentById for multiple buckets)
  setBasePrefix: async (basePrefix: string): Promise<void> => {
    await api.post('/base-prefix', { basePrefix });
  },

  // Set environment by ID (handles multiple environments with same prefix)
  setEnvironmentById: async (environmentId: string): Promise<void> => {
    await api.post('/set-environment', { environmentId });
  },

  // Get all environments with bucket details
  getEnvironmentsWithBuckets: async (): Promise<EnvironmentWithBucket[]> => {
    const { data } = await api.get<EnvironmentWithBucket[]>('/environments-with-buckets');
    return data;
  },

  // Get currently selected environment from backend
  getCurrentEnvironment: async (): Promise<{ environmentId: string; bucketId: string; prefix: string; environmentName: string }> => {
    const { data } = await api.get('/current-environment');
    return data;
  },
};

// Environment CRUD operations
export interface EnvironmentFile {
  key: string;
  name: string;
  size: number;
  lastModified: string;
}

export interface EnvironmentS3Config {
  name: string;
  awsAccessKeyId: string;
  awsSecretAccessKey: string;
  awsRegion: string;
  awsS3Bucket: string;
  awsS3Endpoint?: string;
  usingGlobal?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CloneSelections {
  [folder: string]: string[]; // folder -> array of file names
}

export const environmentApi = {
  async createEnvironment(name: string): Promise<{ name: string }> {
    const { data } = await api.post('/environments', { name });
    return data;
  },

  async deleteEnvironment(name: string): Promise<void> {
    await api.delete(`/environments/${encodeURIComponent(name)}`);
  },

  async renameEnvironment(currentName: string, newName: string): Promise<{ newName: string }> {
    const { data } = await api.put(`/environments/${encodeURIComponent(currentName)}`, { newName });
    return data;
  },

  // Granular cloning with file selections (legacy synchronous endpoint)
  async cloneEnvironment(source: string, target: string, selections: CloneSelections): Promise<void> {
    await api.post('/environments/clone', { source, target, selections });
  },

  // Granular cloning with job support (streaming endpoint)
  async cloneEnvironmentWithJob(source: string, target: string, selections: CloneSelections): Promise<void> {
    await api.post('/environments/clone-stream', { source, target, selections });
  },

  // Legacy folder-level cloning
  async cloneEnvironmentFolders(source: string, target: string, folders?: string[]): Promise<void> {
    await api.post('/environments/clone-folders', { source, target, folders });
  },

  // Get files in a specific folder for an environment
  async getEnvironmentFolderFiles(environment: string, folder: string): Promise<EnvironmentFile[]> {
    const { data } = await api.get<EnvironmentFile[]>(`/environments/${encodeURIComponent(environment)}/folders/${encodeURIComponent(folder)}`);
    return data;
  },

  // Clean up .gitkeep files from an environment
  async cleanupGitkeepFiles(environment: string): Promise<void> {
    await api.post(`/environments/${encodeURIComponent(environment)}/cleanup-gitkeep`);
  },

  // Rebuild CMI5 activity index for an environment
  async rebuildCMI5Index(environment: string): Promise<{ success: boolean; message: string }> {
    const { data } = await api.post<{ success: boolean; message: string }>(`/environments/${encodeURIComponent(environment)}/rebuild-cmi5-index`);
    return data;
  },

  // Get environment S3 configuration
  async getEnvironmentConfig(environment: string): Promise<EnvironmentS3Config> {
    const { data } = await api.get<EnvironmentS3Config>(`/environments/${encodeURIComponent(environment)}/config`);
    return data;
  },

  // Update environment S3 configuration
  async updateEnvironmentConfig(environment: string, config: Partial<Omit<EnvironmentS3Config, 'name' | 'createdAt' | 'updatedAt' | 'usingGlobal'>>): Promise<EnvironmentS3Config> {
    const { data } = await api.put<EnvironmentS3Config>(`/environments/${encodeURIComponent(environment)}/config`, config);
    return data;
  },

  // Get CMI5 activities for a course in S3
  async getCMI5ActivitiesForCourse(environment: string, courseId: number): Promise<Array<{
    id: number;
    name: string;
    cmi5activityid: string;
    tenantId: string;
    cmi5CourseId: string;
    key: string;
  }>> {
    const { data } = await api.get(`/environments/${encodeURIComponent(environment)}/courses/${courseId}/cmi5-activities`);
    return data.activities || [];
  },
};

// CSV-specific APIs
export interface CSVData {
  headers: string[];
  rows: Record<string, string>[];
}

export const csvApi = {
  // Parse CSV file
  parseCSV: async (folder: string, filename: string): Promise<CSVData> => {
    const { data } = await api.get<CSVData>(`/csv/${folder}/${filename}`);
    return data;
  },

  // Update CSV file
  updateCSV: async (folder: string, filename: string, data: CSVData): Promise<void> => {
    await api.put(`/csv/${folder}/${filename}`, data);
  },
};

// AU-Scenario Linkage APIs
export interface AUScenarioLinkages {
  linkages: Record<string, string>; // AU ID -> "scenarioUuid:version"
}

export interface SetLinkageResponse {
  success: boolean;
  message: string;
  auId: string;
  scenarioReference: string;
}

export interface RemoveLinkageResponse {
  success: boolean;
  message: string;
  auId: string;
}

export const linkageApi = {
  // Get all AU-scenario linkages for an activity
  getLinkages: async (bucketId: string, environmentId: string, packageKey: string): Promise<Record<string, string>> => {
    const encodedPackageKey = encodeURIComponent(packageKey);
    const { data } = await api.get<AUScenarioLinkages>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/cmi5-packages/${encodedPackageKey}/linkages`
    );
    return data.linkages || {};
  },

  // Update all linkages at once
  updateLinkages: async (
    bucketId: string,
    environmentId: string,
    packageKey: string,
    linkages: Record<string, string>
  ): Promise<void> => {
    const encodedPackageKey = encodeURIComponent(packageKey);
    await api.put(
      `/s3-buckets/${bucketId}/environments/${environmentId}/cmi5-packages/${encodedPackageKey}/linkages`,
      { linkages }
    );
  },

  // Set a single AU-scenario linkage
  setLinkage: async (
    bucketId: string,
    environmentId: string,
    packageKey: string,
    auId: string,
    scenarioUuid: string,
    version: string
  ): Promise<SetLinkageResponse> => {
    const encodedPackageKey = encodeURIComponent(packageKey);
    const encodedAuId = encodeURIComponent(auId);
    const { data } = await api.post<SetLinkageResponse>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/cmi5-packages/${encodedPackageKey}/linkages/${encodedAuId}`,
      { scenarioUuid, version }
    );
    return data;
  },

  // Remove an AU-scenario linkage
  removeLinkage: async (
    bucketId: string,
    environmentId: string,
    packageKey: string,
    auId: string
  ): Promise<RemoveLinkageResponse> => {
    const encodedPackageKey = encodeURIComponent(packageKey);
    const encodedAuId = encodeURIComponent(auId);
    const { data } = await api.delete<RemoveLinkageResponse>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/cmi5-packages/${encodedPackageKey}/linkages/${encodedAuId}`
    );
    return data;
  },
};

// RangeOS Scenario Storage APIs
export interface ScenarioInfo {
  uuid: string;
  name: string;
  rangeosApiUrl: string;
  designId?: string;
}

export interface ScenarioMetadata {
  currentVersion: string;
  versionManifestKey: string;
  lastModified: string;
  fileName: string;
  scenarioInfo: ScenarioInfo;
}

export interface ScenarioListItem {
  uuid: string;
  metadata: ScenarioMetadata | null;
}

export const scenarioStorageApi = {
  // List all RangeOS scenario drafts in an environment
  listScenarios: async (bucketId: string, environmentId: string): Promise<ScenarioListItem[]> => {
    const { data } = await api.get<{ scenarios: ScenarioListItem[] }>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/rangeos-scenarios`
    );
    return data.scenarios || [];
  },

  // Get version history for a scenario
  getScenarioVersions: async (bucketId: string, environmentId: string, scenarioUuid: string): Promise<VersionMetadata[]> => {
    const { data } = await api.get<{ versions: VersionMetadata[] }>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/rangeos-scenarios/${scenarioUuid}/versions`
    );
    return data.versions || [];
  },
};

// Course-Activity Linkage APIs
export interface CourseActivityLinkages {
  linkages: Record<string, string>; // Activity filename -> version
}

export interface SetActivityLinkageResponse {
  success: boolean;
  message: string;
  activityFilename: string;
  version: string;
}

export interface RemoveActivityLinkageResponse {
  success: boolean;
  message: string;
  activityFilename: string;
}

export const courseLinkageApi = {
  // Get all course-activity linkages for a course
  getCourseLinkages: async (bucketId: string, environmentId: string, courseKey: string): Promise<Record<string, string>> => {
    const encodedCourseKey = encodeURIComponent(courseKey);
    const { data } = await api.get<CourseActivityLinkages>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/courses/${encodedCourseKey}/linkages`
    );
    return data.linkages || {};
  },

  // Update all course-activity linkages at once
  updateCourseLinkages: async (
    bucketId: string,
    environmentId: string,
    courseKey: string,
    linkages: Record<string, string>
  ): Promise<void> => {
    const encodedCourseKey = encodeURIComponent(courseKey);
    await api.put(
      `/s3-buckets/${bucketId}/environments/${environmentId}/courses/${encodedCourseKey}/linkages`,
      { linkages }
    );
  },

  // Set a single course-activity linkage
  setActivityLinkage: async (
    bucketId: string,
    environmentId: string,
    courseKey: string,
    activityFilename: string,
    version: string
  ): Promise<SetActivityLinkageResponse> => {
    const encodedCourseKey = encodeURIComponent(courseKey);
    const encodedActivityFilename = encodeURIComponent(activityFilename);
    const { data } = await api.post<SetActivityLinkageResponse>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/courses/${encodedCourseKey}/linkages/${encodedActivityFilename}`,
      { version }
    );
    return data;
  },

  // Remove a course-activity linkage
  removeActivityLinkage: async (
    bucketId: string,
    environmentId: string,
    courseKey: string,
    activityFilename: string
  ): Promise<RemoveActivityLinkageResponse> => {
    const encodedCourseKey = encodeURIComponent(courseKey);
    const encodedActivityFilename = encodeURIComponent(activityFilename);
    const { data } = await api.delete<RemoveActivityLinkageResponse>(
      `/s3-buckets/${bucketId}/environments/${environmentId}/courses/${encodedCourseKey}/linkages/${encodedActivityFilename}`
    );
    return data;
  },
};

export default api;
